import merge from 'lodash/merge'
import D2C_Entity from './Entity'
import api from '@/api'
import logger from '@/logger'
import logos from '@/logos'
import objects from '@/objects'
import store from '@/store'

class D2C_Host extends D2C_Entity {
  target = 'host'
  isCloud = false
  isCustom = false
  hasPage = true
  static objectType = 'entityType'
  static typeName = 'host'
  static active = false
  static statuses = {
    NOT_CREATED: 'NOT_CREATED',
    OFFLINE: 'OFFLINE',
    ONLINE: 'ONLINE',
  }
  static processes = {
    DESTROYING: 'DESTROYING',
    QUEUED: 'QUEUED',
    SETUP: 'SETUP',
    STARTING: 'STARTING',
    STOPPING: 'STOPPING',
  }
  static actions = {
    SETUP: 'SETUP',
    START: 'START',
    STOP: 'STOP',
  }
  static methods = {
    resetup: true,
    terminal: true,
    update: true,
  }
  static fields = {
    ...D2C_Entity.fields,
    amt: {
      default: 1,
      label: 'Amount',
      max: 5,
      min: 1,
      required: false,
      width: 3,
      validate(value, entity, mode) {
        if (value < this.min) {
          return 'Do you want to create a host or not?'
        }
        if (mode === 'creatingDemo' && store.getters.hosts.filter(host => host.demo).length + value > this.creatingDemo.max) {
          return `${this.creatingDemo.max} is maximum`
        }
        if (value > this.max) {
          return `${this.max} is maximum`
        }
        return null
      },
      creating: {},
      creatingCustom: {
        readonly: true,
        omit: true,
      },
      creatingDemo: {
        max: 3,
      },
    },
    id: {
      hidden: true,
      validate(value) {
        if (!value) {
          return 'Host is not selected'
        }
        return null
      },
      importing: {},
    },
    keys: {
      default: [],
      label: 'Public keys',
      multiple: true,
      placeholder: 'Select...',
      placeholderEmpty: 'No available keys',
      required: false,
      width: 14,
      validate() {
        return null
      },
      updating: {},
    },
    name: {
      default: '',
      label: 'Name',
      required: true,
      width: 13,
      validate(value, entity, mode) {
        const regex = /^[a-zA-Z][a-zA-Z0-9-]{0,15}$/
        if (value === 'localhost') {
          return 'Reserved name'
        }
        if (!regex.test(value)) {
          return 'Invalid name'
        }
        if (mode !== 'creatingDemo' && !entity.created && store.getters.hostsByName[value]) {
          return 'This name is already used'
        }
        return (value && value.length > 0) ? null : 'Must be filled'
      },
      creating: {},
      creatingCustom: {},
      creatingDemo: {
        label: 'Group name',
        omit: true,
        readonly: true,
        required: false,
        width: 10,
      },
    },
    labels: {
      allowAdditions: true,
      default: [],
      label: 'Labels',
      multiple: true,
      placeholder: 'Select an existing label or add a new one...',
      placeholderEmpty: 'Add a new label...',
      required: false,
      validate() {
        return null
      },
      creating: {},
      creatingCustom: {},
    },
    rules: {
      default: {
        global: [],
        local: [],
      },
      label: 'Rules',
      validate() {
        return null
      },
      updatingAccess: {}
    },
  }
  static stats = {
    cpu: {},
    mem: {},
    main_disk: {},
  }
  static create(props = {}) {
    const provider = props.provider && store.getters.providersById[props.provider]
    const type = provider ? provider.type : props.providerType
    const Constructor = type ? objects._hostsByType[type] : (props.cloud ? objects.HostCloud : null)
    if (Constructor) {
      return new Constructor(props)
    }
    if (props.providerType) {
      logger.warning(`🚨 Unknown host type: ${props.providerType}`)
    }
    return new D2C_Host(props)
  }
  get _actions() {
    const role = store.getters.role
    const member = store.getters.member
    const rules = {}
    const actions = ['hostInfo', 'hostUpdate', 'hostUsage', 'hostTerminal', 'hostDestroy']
    for (const action of actions) {
      rules[action] = member.isOwner ? 1 : merge(
        {result: (this.rules.global[role.id] || {actions: {}}).actions[action]},
        {result: (this.rules.global[member.id] || {actions: {}}).actions[action]},
        {result: (this.rules.local[role.id] || {actions: {}}).actions[action]},
        {result: (this.rules.local[member.id] || {actions: {}}).actions[action]}
      ).result
    }
    return rules
  }
  get _path() {
    return this.id && this.name ? `/hosts/${encodeURIComponent(this.name)}` : null
  }
  get _containers() {
    return store.getters.containersByHost[this.id] || []
  }
  get _projects() {
    return store.getters.projectsByHost[this.id] || []
  }
  get _services() {
    return store.getters.servicesByHost[this.id] || []
  }
  get _isCreated() {
    return this.status !== this.constructor.statuses.NOT_CREATED
  }
  get _isSetup() {
    return this.deployable
  }
  get _isOnline() {
    return this.status === this.constructor.statuses.ONLINE
  }
  get _isOffline() {
    return this.status === this.constructor.statuses.OFFLINE
  }
  get _isConnectable() {
    return this.ipAddress && this._isCreated
  }
  get _isConnecting() {
    return this.process === this.constructor.processes.AWAITING_CONNECTION
  }
  get _hasIP() {
    return Boolean(this.ipAddress)
  }
  get _logo() {
    return this._provider ? this._provider._logo : logos.providers[this.providerType]
  }
  get _instanceData() {
    return this._instance || this.instanceData
  }
  get _regionData() {
    return this._region || this.regionData
  }
  get _instanceTitle() {
    return this.storageSize ? `${this._instance._title}, ${this.storageSize}GB ${this.storageType}` : this._instance._fullTitle
  }
  get _stats() {
    return Object.assign({}, this.constructor.stats, store.state.hostStats[this.id])
  }
  get _ports() {
    let ports = []
    for (const service of this._services) {
      ports = ports.concat(service.ports.map(port => ({...port, serviceId: service.id, remote: service.remoteAccess})))
    }
    ports.sort((a, b) => a.value > b.value ? 1 : -1)
    ports.sort(a => a.protocol === 'TCP' ? 1 : -1)
    return ports
  }
  get _remotePorts() {
    return this._ports.filter(port => port.remote)
  }
  applyAction(action, params = {}) {
    return api.applyHostAction(this.id, action, params)
  }
  recreate() {
    return this.applyAction(this.constructor.actions.RECREATE)
  }
  restart() {
    return this.applyAction(this.constructor.actions.RESTART)
  }
  setup(list = []) {
    return this.applyAction(this.constructor.actions.SETUP, {list})
  }
  start() {
    return this.applyAction(this.constructor.actions.START)
  }
  stop() {
    return this.applyAction(this.constructor.actions.STOP)
  }
  updateInstance(instance, reversible = true) {
    return api.updateHostInstance(this.id, instance, reversible)
  }
  updateDisk(size) {
    return api.updateHostDisk(this.id, size)
  }
  cancelTask() {
    return api.cancelHostTask(this.id)
  }
  saveKeys(keys) {
    return api.saveHostKeys(this.id, keys)
  }
  fetchLogs(filter) {
    return api.fetchHostLogs(this.id, filter)
  }
  fetchStats(filter) {
    return api.fetchHostStats(this.id, filter)
  }
  subscribeStats() {
    api.subscriptions.hostStats[this.id] = api.subscriptions.hostStats[this.id] ? api.subscriptions.hostStats[this.id] + 1 : 1
    api.subscriptions.updateHostStats()
  }
  unsubscribeStats() {
    api.subscriptions.hostStats[this.id] = api.subscriptions.hostStats[this.id] ? api.subscriptions.hostStats[this.id] - 1 : 0
    api.subscriptions.updateHostStats()
  }
  create() {
    if (this.demo) {
      return api.createDemoHosts(this.getValues('creatingDemo'))
    }
    store.dispatch('update', {lastProviderId: this.provider})
    return api.createHost(this.constructor.providerType, this.getValues('creating'))
  }
  destroy(force = false) {
    return api.destroyHost(this.id, force)
  }
  forceDestroy() {
    return this.destroy(true)
  }
  updateRules(newRules) {
    return api.updateHostRules(this.id, newRules || this.rules)
  }
}

export default D2C_Host
